import { useEffect, useState } from 'react';
import styles from './styles.module.scss';
import axios from 'axios';
import { BounceLoader } from 'react-spinners';
import { useSession } from 'next-auth/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faChevronDown, faChevronLeft, faChevronRight, faEdit, faEye, faTrashCan, faXmark } from '@fortawesome/free-solid-svg-icons';

import errorMsg from '../../../../../Functions/errorMsg';
import successMsg from '../../../../../Functions/successMsg';

import formatCurrency from '../../../../../Functions/formatCurrency';

export default function Overview({ openCreateService, openEditService }) {
  const { data: session, status } = useSession();
  
  const [loadingData, setLoadingData] = useState(false);
  const [servicesData, setServicesData] = useState(null);
  const [hasError, setHasError] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [confirmDelete, setConfirmDelete] = useState(null); // State to handle delete confirmation
  const [loadingDelete, setLoadingDelete] = useState(false); // State for delete loading spinner

  useEffect(() => {
    if (status !== 'authenticated' || hasError || loadingData || servicesData) return;

    async function fetchServicesData() {
      setLoadingData(true);
      try {
        const getResult = await axios.get(`${window.location.origin}/api/vendor/manage/services/servicesData`);

        if (getResult && getResult.status === 200) {
          setServicesData(getResult.data);
        }
      } catch (error) {
        errorMsg(error?.response?.data?.status || error.toString());
        setHasError(true);
      } finally {
        setLoadingData(false);
      }
    }

    if (session && status === 'authenticated') {
      fetchServicesData();
    }
  }, [hasError, session, status, loadingData, servicesData]);

  const maxPages = servicesData ? Math.ceil(servicesData.length / itemsPerPage) : 1;

  const handlePageChange = (values) => {
    const value = parseInt(values.value);
    if (!isNaN(value) && value >= 1 && value <= maxPages) {
      setCurrentPage(value);
    }
  };

  const isAllowed = (values) => {
    const { floatValue } = values;
    return floatValue >= 1 && floatValue <= maxPages;
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < maxPages) setCurrentPage(currentPage + 1);
  };

  function onChangeItemsPerPage(num) {
    setItemsPerPage(parseInt(num));
    setCurrentPage(1); // Reset to the first page when items per page changes
  }

  const confirmDeleteService = (serviceID) => {
    setConfirmDelete(serviceID); // Set the service ID to confirm deletion
  };

  const handleDeleteService = async () => {
    if (!confirmDelete) return;

    setLoadingDelete(true);
    try {
      const result = await axios.post(`${window.location.origin}/api/vendor/manage/services/deleteService`, {
        serviceID: confirmDelete,
      });

      if (result && result.status === 200) {
        successMsg('Service deleted successfully.');
        
        // Filter out the deleted service from servicesData
        setServicesData(servicesData.filter(service => service.serviceID !== confirmDelete));
      }
    } catch (error) {
      errorMsg(error?.response?.data?.status || error.toString());
    } finally {
      setLoadingDelete(false);
      setConfirmDelete(null); // Close the confirmation modal
    }
  };

  const handleCancelDelete = () => {
    setConfirmDelete(null); // Close the confirmation modal
  };

  if (loadingData || !servicesData || !session || status !== 'authenticated' || loadingDelete || hasError) {
    return (
      <section id={styles.loading}>
        <BounceLoader size={75} speedMultiplier={2} color="#154734" />
      </section>
    );
  }

  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = servicesData.slice(startIndex, startIndex + itemsPerPage);

  return (
    <section id={styles.wrapper}>
      <section id={styles.listings}>
        <nav id={styles.pageNav}>
          <section>
            <FontAwesomeIcon
              icon={faChevronLeft}
              id={styles.navIcon}
              onClick={handlePreviousPage}
            />
            <section>
              <h1>{currentPage} / {maxPages}</h1>
            </section>
            <FontAwesomeIcon
              icon={faChevronRight}
              id={styles.navIcon}
              onClick={handleNextPage}
            />
          </section>
          <section>
            <h1>Per Page: </h1>
            <BusinessCategoryDropMenu 
              numbers={[ '12', '24', '36', '48' ]}
              selected={itemsPerPage}
              onChangeItemsPerPage={onChangeItemsPerPage} 
            />
          </section>
        </nav>
        
        <section id={styles.serviceList}>

        {confirmDelete && (
        <DeleteConfirmationModal 
          onCancel={handleCancelDelete} 
          onConfirm={handleDeleteService} 
        />
        )}

        <ServiceInfoContainer />
          { currentItems.map((service, index) => {
            return (
              <div key={index} style={{ display: 'flex', flexDirection: 'column' }}>
                <ServiceCard data={service} openEditService={openEditService} confirmDeleteService={confirmDeleteService} />
                <hr className={styles.lineSeparator} />
              </div>
            )
          }) }
        </section>
      </section>
      <section id={styles.filters}>
        <div onClick={openCreateService}>
          <h1>Create Service</h1>
        </div>
        <div>
          
        </div>
      </section>
      

    </section>
  );
}

const ServiceInfoContainer = () => {
  const [isChecked, setIsChecked] = useState(false);

  function handleAllSelected() {
    setIsChecked(!isChecked);
  }

  return (
    <section className={styles.serviceInfoContainer}>
      <label className={styles.customCheckbox}>
        <input 
          type="checkbox" 
          checked={isChecked} 
          onChange={handleAllSelected} 
        />
        {isChecked && (
          <FontAwesomeIcon icon={faCheck} className={styles.icon} />
        )}
      </label>
      <h1>Name</h1>
      <h1>Price</h1>
      <h1>Status</h1>
      <h1>Actions</h1>
    </section>
  );
};

const ServiceCard = ({ data, openEditService, openViewService, confirmDeleteService }) => {
  const serviceID = data.serviceID;

  const [isChecked, setIsChecked] = useState(false);

  const handleCheckboxChange = () => {
    setIsChecked(!isChecked);
  };

  function returnStatusClassType() {
    const status = data.status;
    if(status === 'Draft') return styles.draft;
    if(status === 'Active') return styles.active;
    if(status === 'Inactive') return styles.inactive;
  }

  return (
    <section className={styles.serviceItem}>
      <label className={styles.customCheckbox}>
        <input 
          type="checkbox" 
          checked={isChecked} 
          onChange={handleCheckboxChange} 
        />
        {isChecked && (
          <FontAwesomeIcon icon={faCheck} className={styles.icon} />
        )}
      </label>
      <h1>{data.name}</h1>
      <h1>{formatCurrency(data.cost)}</h1>
      <section id={styles.statusContainer}>
        <h1 className={returnStatusClassType()}>{data.status}</h1>
      </section>
      <section id={styles.actionBar}>
        <FontAwesomeIcon icon={faEdit} id={styles.editIcon} onClick={() => openEditService(serviceID)} />
        { /* <FontAwesomeIcon icon={faEye} id={styles.viewIcon} onClick={() => openViewService(serviceID)} /> */ }
        <FontAwesomeIcon icon={faTrashCan} id={styles.deleteIcon} onClick={() => confirmDeleteService(serviceID)} />
      </section>
    </section>
  )};
  
  const BusinessCategoryDropMenu = ({ numbers, selected, onChangeItemsPerPage }) => {
    const [navDropMenu, setNavDropMenu] = useState(false);
    
    const handleSelection = (e, num) => {
      e.preventDefault();
      onChangeItemsPerPage(parseInt(num));
      setNavDropMenu(false);
    };
  
    return (
      <div onMouseLeave={() => setNavDropMenu(false)} id={styles.categoryDrop}>
        <button 
          onClick={(e) => { e.preventDefault(); setNavDropMenu(!navDropMenu); }} 
          className={styles.dropdownBtn}
        >
          <FontAwesomeIcon icon={faChevronDown} className={styles.icon} /> 
          {selected}
        </button>
        <div style={{ display: navDropMenu ? 'block' : 'none' }} className={styles.dropdownContent}>
          {numbers.map((num, index) => (
            <button key={index} onClick={(e) => handleSelection(e, num)}>
              {num}
            </button>
          ))}
        </div>
      </div>
    );
  };

const DeleteConfirmationModal = ({ onCancel, onConfirm }) => {
  return (
    <section id={styles.confirmDeletion}>
      <div>
          <div>
              <h1>Confirm Deletion</h1>
              <span>
                  <button id={styles.confirm} onClick={onConfirm}>
                      <FontAwesomeIcon icon={faCheck} id={styles.icon} />
                  </button>
                  <button id={styles.cancel} onClick={onCancel}>
                      <FontAwesomeIcon icon={faXmark} id={styles.icon} />
                  </button>
              </span>
          </div>
      </div>
    </section>
  );
};
